import React from 'react';
import { Box, Typography, Popover, Tooltip, Skeleton } from '@mui/material';
import { GridView } from '@mui/icons-material';
import { colors } from '../../themes/typography';
import { getExperimentStatusColor } from '../../themes/experimentColors';

/**
 * SlotPicker - Compact inline slot selector with chip/pill toggles
 * and an optional popover mini-grid for spatial context.
 */
const SlotPicker = ({ slots, productId, selectedSlots, onChange, loading, t }) => {
  const [gridAnchor, setGridAnchor] = React.useState(null);

  // Filter slots for this product
  const productSlots = React.useMemo(() => {
    if (!slots || !productId) return [];
    return slots.filter(s => s.dProductId === productId);
  }, [slots, productId]);

  // Group product slots by row
  const slotsByRow = React.useMemo(() => {
    const grouped = new Map();
    productSlots.forEach(slot => {
      const row = slot.row;
      if (!grouped.has(row)) grouped.set(row, []);
      grouped.get(row).push(slot);
    });
    grouped.forEach(rowSlots => rowSlots.sort((a, b) => a.column - b.column));
    return grouped;
  }, [productSlots]);

  const hasMultipleRows = slotsByRow.size > 1;

  // Available = no active experiment
  const availableSlotIds = React.useMemo(() => {
    return new Set(productSlots.filter(s => !s.activeExperimentId).map(s => s.selectionId));
  }, [productSlots]);

  // Auto-select all available slots when productId changes
  React.useEffect(() => {
    if (productSlots.length > 0) {
      const available = productSlots
        .filter(s => !s.activeExperimentId)
        .map(s => s.selectionId);
      onChange(available);
    }
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grid dimensions for popover (all slots, not just product)
  const gridDimensions = React.useMemo(() => {
    if (!slots || slots.length === 0) return { rows: 0, columns: 0 };
    const maxRow = Math.max(...slots.map(s => s.row));
    const maxCol = Math.max(...slots.map(s => s.column));
    return { rows: maxRow + 1, columns: maxCol + 1 };
  }, [slots]);

  const productSlotSet = React.useMemo(() => {
    return new Set(productSlots.map(s => `${s.row}-${s.column}`));
  }, [productSlots]);

  const toggleSlot = (selectionId) => {
    if (!availableSlotIds.has(selectionId)) return;
    const newSelected = selectedSlots.includes(selectionId)
      ? selectedSlots.filter(id => id !== selectionId)
      : [...selectedSlots, selectionId];
    onChange(newSelected);
  };

  const selectAll = () => onChange([...availableSlotIds]);
  const deselectAll = () => onChange([]);

  const allSelected = availableSlotIds.size > 0 && selectedSlots.length === availableSlotIds.size;

  if (loading) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <Skeleton variant="rounded" height={56} sx={{ borderRadius: '8px' }} />
      </Box>
    );
  }

  if (productSlots.length === 0) return null;

  return (
    <Box sx={{
      mt: 1.5,
      px: 1.5,
      py: 1.25,
      borderRadius: '8px',
      border: `1px solid ${colors.border.medium}`,
      backgroundColor: colors.background.surface,
    }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{
          fontSize: '12px',
          fontWeight: 500,
          color: '#374151',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        }}>
          {t('slotsColumn', { defaultValue: 'Carriles' })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            onClick={allSelected ? deselectAll : selectAll}
            sx={{
              fontSize: '11px',
              color: colors.primary.main,
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {allSelected
              ? t('deselectAll', { defaultValue: 'Deseleccionar todo' })
              : t('selectAll', { defaultValue: 'Seleccionar todo' })
            }
          </Typography>
          {slots.length > productSlots.length && (
            <Tooltip title={t('machineLayout', { defaultValue: 'Diseño de máquina' })} arrow enterDelay={300}>
              <Box
                onClick={(e) => setGridAnchor(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: colors.text.muted,
                  transition: 'all 150ms ease',
                  '&:hover': { backgroundColor: colors.border.light, color: colors.text.primary }
                }}
              >
                <GridView sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Chip selector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {[...slotsByRow.entries()].map(([rowNum, rowSlots]) => (
          <Box key={rowNum} sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            {hasMultipleRows && (
              <Typography sx={{
                fontSize: '9px',
                fontWeight: 600,
                color: colors.text.muted,
                width: 18,
                flexShrink: 0,
                textAlign: 'center',
                fontFamily: 'SF Mono, Monaco, Consolas, monospace',
              }}>
                R{rowNum + 1}
              </Typography>
            )}
            {rowSlots.map(slot => {
              const isSelected = selectedSlots.includes(slot.selectionId);
              const isDisabled = !!slot.activeExperimentId;
              const statusColor = isDisabled ? getExperimentStatusColor(slot.activeExperimentStatus) : null;

              return (
                <Tooltip
                  key={slot.selectionId}
                  title={isDisabled
                    ? t('usedByExperiment', { id: slot.activeExperimentId, defaultValue: `Usado por experimento #${slot.activeExperimentId}` })
                    : t('slotSingle', { slot: slot.selectionId, defaultValue: `Carril ${slot.selectionId}` })
                  }
                  enterDelay={400}
                  arrow
                >
                  <Box
                    onClick={() => toggleSlot(slot.selectionId)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      padding: '3px 10px',
                      fontSize: '12px',
                      fontWeight: 600,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                      letterSpacing: '-0.01em',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      transition: 'all 150ms ease',
                      minWidth: 32,
                      height: 26,
                      userSelect: 'none',
                      ...(isDisabled ? {
                        backgroundColor: '#F3F4F6',
                        color: '#9CA3AF',
                        border: `1.5px solid ${statusColor}`,
                        opacity: 0.6,
                      } : isSelected ? {
                        backgroundColor: colors.primary.main,
                        color: '#FFFFFF',
                        border: `1.5px solid ${colors.primary.main}`,
                        boxShadow: '0 1px 2px rgba(59, 130, 246, 0.3)',
                      } : {
                        backgroundColor: '#FFFFFF',
                        color: '#6B7280',
                        border: `1.5px solid ${colors.border.dark}`,
                        '&:hover': {
                          borderColor: colors.primary.main,
                          color: colors.primary.main,
                          backgroundColor: '#F8FAFF',
                        }
                      })
                    }}
                  >
                    {slot.selectionId}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Summary */}
      <Typography sx={{
        fontSize: '11px',
        color: colors.text.muted,
        mt: 1,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      }}>
        {t('slotsSelected', {
          selected: selectedSlots.length,
          total: availableSlotIds.size,
          defaultValue: `${selectedSlots.length} de ${availableSlotIds.size} carriles seleccionados`
        })}
      </Typography>

      {/* Popover mini-grid */}
      <Popover
        open={Boolean(gridAnchor)}
        anchorEl={gridAnchor}
        onClose={() => setGridAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
      >
        <Typography sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: '#374151',
          mb: 1.5,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        }}>
          {t('machineLayout', { defaultValue: 'Diseño de máquina' })}
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridDimensions.columns}, 26px)`,
          gridTemplateRows: `repeat(${gridDimensions.rows}, 26px)`,
          gap: '3px',
        }}>
          {slots.map(slot => {
            const isProductSlot = productSlotSet.has(`${slot.row}-${slot.column}`);
            const isSelected = selectedSlots.includes(slot.selectionId);
            const isDisabled = isProductSlot && !!slot.activeExperimentId;

            return (
              <Tooltip
                key={`grid-${slot.selectionId}`}
                title={isProductSlot
                  ? (isDisabled
                    ? t('usedByExperiment', { id: slot.activeExperimentId, defaultValue: `#${slot.activeExperimentId}` })
                    : t('slotSingle', { slot: slot.selectionId, defaultValue: `Carril ${slot.selectionId}` }))
                  : (slot.productName || '')
                }
                enterDelay={200}
                arrow
              >
                <Box
                  onClick={() => isProductSlot && toggleSlot(slot.selectionId)}
                  sx={{
                    gridColumn: slot.column + 1,
                    gridRow: slot.row + 1,
                    width: 26,
                    height: 26,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '8px',
                    fontWeight: 600,
                    fontFamily: 'SF Mono, Monaco, Consolas, monospace',
                    cursor: isProductSlot && !isDisabled ? 'pointer' : 'default',
                    transition: 'all 100ms ease',
                    ...(isProductSlot ? (
                      isDisabled ? {
                        backgroundColor: '#F3F4F6',
                        border: `1.5px solid ${getExperimentStatusColor(slot.activeExperimentStatus)}`,
                        color: '#9CA3AF',
                      } : isSelected ? {
                        backgroundColor: colors.primary.main,
                        border: `1.5px solid ${colors.primary.main}`,
                        color: '#FFF',
                      } : {
                        backgroundColor: '#FFF',
                        border: `1.5px solid ${colors.primary.main}`,
                        color: colors.primary.main,
                        '&:hover': { backgroundColor: '#EFF6FF' }
                      }
                    ) : {
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      color: '#D1D5DB',
                    })
                  }}
                >
                  {isProductSlot ? slot.selectionId : ''}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
};

export default SlotPicker;
